import { Typography } from '@mui/material'
import { UseFormReturn } from 'react-hook-form'
import { Resource } from 'utoniq-ui'

import {
  EventInputDateCell,
  EventInputLocationCell,
  EventPublishDateCell,
  TextInputCell,
  ThumbnailUploadCell,
  ToggleInputCell,
} from '@/components/pages/ConsoleEventCreate/template/components'
import { CreateEventValue } from '@/components/pages/ConsoleEventCreate/template/EventCreateForm'
import { GPSMapModalProps } from '@/components/pages/ConsoleMissionCreate/template/GPSMapModal'
import { tLabel, tMessage, UseFormProps, useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'

export type EventEditFormProps = {
  artistId: string
  method: UseFormReturn<EditEventValue>
  fileUploadInfo: FileUploadInfo
  gpsClientInfo: {
    permission: boolean
    currentLocation?: GPSMapModalProps['currentData']
  }
  onPlaceSearch: GPSMapModalProps['onSearch']
  predictions: GPSMapModalProps['predictions']
  place: GPSMapModalProps['place']
  onSelectPlace: GPSMapModalProps['onSelectPlace']
  onRequestGPSPermission: () => void
}

export type EditEventValue = CreateEventValue & {}

export type FileUploadInfo = {
  onDrop: () => Promise<Resource | undefined>
  progress: number
}

export const schema: UseFormProps['schema'] = o => ({
  title: o.string().max(100).label(tLabel('consoleEventEdit.title')),
  description: o
    .string()
    .max(1000)
    .label(tLabel('consoleEventEdit.description')),
  startedAt: o
    .date()
    .allow(null, '')
    .optional()
    .label(tLabel('consoleEventEdit.startedAt')),
  endedAt: o
    .date()
    .allow(null, '')
    .optional()
    .label(tLabel('consoleEventEdit.endedAt')),
  closeDate: o
    .array()
    .items({
      //id of field array
      _id: o.string().optional(),
      date: o
        .date()
        .allow(null, '')
        .optional()
        .label(tLabel('consoleEventEdit.closeDate')),
    })
    .allow(null)
    .optional(),
  location: o
    .object({
      latitude: o
        .number()
        .optional()
        .allow(null, '')
        .label(tLabel('consoleEventEdit.location.latitude')),
      longitude: o
        .number()
        .optional()
        .allow(null, '')
        .label(tLabel('consoleEventEdit.location.longitude')),
      approximateAddress: o.string().when('latitude', {
        is: 0,
        then: o.string().when('mapUri', {
          is: o.string().exist(),
          then: o
            .string()
            .max(50)
            .label(tLabel('consoleEventEdit.location.approximateAddress')),
          otherwise: o
            .string()
            .max(50)
            .optional()
            .allow(null, '')
            .label(tLabel('consoleEventEdit.location.approximateAddress')),
        }),
        otherwise: o
          .string()
          .max(50)
          .label(tLabel('consoleEventEdit.location.approximateAddress')),
      }),
      mapUri: o.string().when('latitude', {
        is: 0,
        then: o
          .string()
          .uri()
          .optional()
          .allow(null, '')
          .label(tLabel('consoleEventEdit.location.mapUri')),
        otherwise: o
          .string()
          .uri()
          .label(tLabel('consoleEventEdit.location.mapUri')),
      }),
    })
    .allow(null)
    .optional(),
  thumbnailPath: o.string().label(tLabel('consoleEventEdit.thumbnailPath')),
  note: o
    .string()
    .max(1000)
    .optional()
    .allow(null, '')
    .label(tLabel('consoleEventEdit.note')),
  thumbnailResource: o.object().allow(null).optional(),
  isPublished: o.boolean(),
  publishStartedAt: o
    .date()
    .allow(null, '')
    .optional()
    .label(tLabel('consoleEventEdit.publishStartedAt')),
  publishEndedAt: o
    .date()
    .allow(null, '')
    .optional()
    .custom((value: Date, helpers) => {
      const [data] = helpers?.state?.ancestors || []
      if (!value) {
        return value
      }
      const publishStartedAt = data?.publishStartedAt || new Date()
      if (publishStartedAt > value) {
        // 開始日が終了日を過ぎていたらエラー
        return helpers.message({
          custom: tMessage('consoleEventEdit.publishEndedAt.custom.over'),
        })
      }
      return value
    })
    .label(tLabel('consoleEventEdit.publishEndedAt')),
})

export const EventEditForm: Organism<EventEditFormProps> = ({
  artistId,
  method,
  fileUploadInfo,
  ...gpsModalProps
}) => {
  const { t } = useTranslation('consoleEventEdit')
  const {
    watch,
    setValue,
    control,
    register,
    trigger,
    formState: { errors },
  } = method

  return (
    <OrganismFrame sx={{ pt: 5 }}>
      <ThumbnailUploadCell
        watch={watch}
        setValue={setValue}
        error={errors?.thumbnailPath}
        fileUploadInfo={fileUploadInfo}
      />
      <TextInputCell
        type="text"
        {...register('title')}
        error={errors?.title}
        displayRequire={{ display: true }}
        displayCount={{
          display: true,
          count: watch('title')?.length,
          limit: 100,
        }}
      />
      <TextInputCell
        type="textarea"
        textFieldProps={{
          multiline: true,
          rows: 5,
          InputProps: { sx: { px: 4, py: 3 } },
        }}
        {...register('description')}
        error={errors?.description}
        displayRequire={{ display: true }}
        displayCount={{
          display: true,
          count: watch('description')?.length,
          limit: 1000,
        }}
      />
      <TextInputCell
        type="textarea"
        textFieldProps={{
          multiline: true,
          rows: 5,
          InputProps: { sx: { px: 4, py: 3 } },
        }}
        {...register('note')}
        error={errors?.note}
        displayCount={{
          display: true,
          count: watch('note')?.length,
          limit: 1000,
        }}
      />
      <EventInputDateCell
        errors={errors}
        control={control}
        setValue={setValue}
        watch={watch}
        startedAt={watch('startedAt')}
        endedAt={watch('endedAt')}
      />
      <EventInputLocationCell
        artistId={artistId}
        location={watch('location')}
        setValue={setValue}
        inputApproximateAddress={{
          ...register('location.approximateAddress'),
          error: errors?.location?.approximateAddress,
        }}
        inputMapUri={{
          ...register('location.mapUri'),
          error: errors?.location?.mapUri,
        }}
        {...gpsModalProps}
      />
      <ToggleInputCell
        content={{
          title: t('isPublishedTitle'),
          description: (
            <Typography variant="caption" color="text.secondary">
              {t('isPublishedDesc')}
            </Typography>
          ),
        }}
        {...register('isPublished')}
        checked={watch('isPublished')}
      />
      <EventPublishDateCell
        watch={watch}
        setValue={setValue}
        errors={[errors.publishStartedAt, errors.publishEndedAt]}
        trigger={trigger}
      />
    </OrganismFrame>
  )
}

export const defaultValues: EditEventValue = {
  title: '',
  description: '',
  thumbnailPath: '',
  thumbnailResource: null,
  note: '',
  startedAt: null,
  endedAt: null,
  location: {
    latitude: 0,
    longitude: 0,
    approximateAddress: '',
    mapUri: '',
  },
  closeDate: [{ date: null }],
  isPublished: false,
  publishStartedAt: null,
  publishEndedAt: null,
}
